import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE, AgentRule } from "../types";
import { ResolvedAgentPaths } from "./paths";

/**
 * One row in the rendered agent registry table. Each phased-agent
 * bundle that previously shipped its own `<bundle>-workflow` rule
 * contributes exactly one entry here so the registry can answer
 * "which agent handles X" without rendering 18 prose summaries
 * into CLAUDE.md.
 */
export interface AgentRegistryEntry {
  /** Bundle name as it appears in `buildBuiltInBundles`, e.g. `bcm-writer`. */
  readonly bundle: string;
  /** Primary user-invocable skill, with leading slash, e.g. `/write-bcm`. */
  readonly skill: string;
  /** Sub-agent name in `.claude/agents/`, e.g. `bcm-writer`. */
  readonly agent: string;
  /**
   * Function that resolves the canonical output path for this
   * bundle from the project's resolved agent-path roots. Returning
   * an empty string signals "no filesystem output path" (used by
   * pr-review). Path-aware so consumer overrides on
   * `AgentConfigOptions.paths` propagate into the rendered table.
   */
  readonly resolveOutputPath: (paths: ResolvedAgentPaths) => string;
  /**
   * One-line purpose description. Lifted from the first prose
   * sentence of the original `<bundle>-workflow` rule so consumers
   * keep the same routing signal.
   */
  readonly purpose: string;
  /**
   * Name of the original `<bundle>-workflow` rule. Used by the
   * registry helper to filter the resolved bundle list and assert
   * (via the test suite) that no bundle still ships its workflow
   * rule into the Claude platform output.
   */
  readonly workflowRuleName: string;
}

/**
 * Static registry of every phased-agent bundle that contributes a
 * routing row. Order is alphabetical by bundle name so the
 * rendered table is stable across runs and consumer-side diffs are
 * minimal. Adding a new phased-agent bundle requires appending one
 * row here and suppressing its `<bundle>-workflow` rule via
 * `platforms: { claude: { exclude: true } }`.
 */
export const AGENT_REGISTRY_ENTRIES: ReadonlyArray<AgentRegistryEntry> = [
  {
    bundle: "agenda",
    skill: "/draft-agenda",
    agent: "agenda-analyst",
    resolveOutputPath: (p) => `${p.meetingsRoot}/`,
    purpose:
      "Pre-meeting agenda authoring (2-phase: draft, finalize). Reuses the project's meeting-type taxonomy.",
    workflowRuleName: "meeting-agenda-workflow",
  },
  {
    bundle: "bcm-writer",
    skill: "/write-bcm",
    agent: "bcm-writer",
    resolveOutputPath: (p) => `${p.bcmRoot}/`,
    purpose:
      "BCM capability-model documents (4-phase: outline, scaffold, context, connect). BIZBOK-aligned.",
    workflowRuleName: "bcm-writer-workflow",
  },
  {
    bundle: "business-models",
    skill: "/scan-business-models",
    agent: "business-models-analyst",
    resolveOutputPath: (p) => `${p.docsRoot}/industry-research/`,
    purpose:
      "Osterwalder business-model canvases per industry segment (3-phase: scan, canvas, complete).",
    workflowRuleName: "business-models-workflow",
  },
  {
    bundle: "company-profile",
    skill: "/profile-company",
    agent: "company-profile-analyst",
    resolveOutputPath: (p) => `${p.companiesRoot}/`,
    purpose:
      "Company profiles, notes, and segment competitive analyses (up to 6 phases incl. refresh).",
    workflowRuleName: "company-profile-workflow",
  },
  {
    bundle: "customer-profile",
    skill: "/discover-customers",
    agent: "customer-profile-analyst",
    resolveOutputPath: (p) => `${p.profilesRoot}/customers/`,
    purpose:
      "Customer-archetype discovery, profiles, and competitor feature-mapping (3-phase).",
    workflowRuleName: "customer-profile-workflow",
  },
  {
    bundle: "docs-sync",
    skill: "/docs-sync-pr",
    agent: "docs-sync",
    resolveOutputPath: (p) => `${p.docsRoot}/`,
    purpose:
      "Pre-PR drift detection and mechanical doc fixes (2-phase: scan, fix). Skeleton release.",
    workflowRuleName: "docs-sync-workflow",
  },
  {
    bundle: "industry-discovery",
    skill: "/discover-industries",
    agent: "industry-discovery-analyst",
    resolveOutputPath: (p) => `${p.industriesRoot}/`,
    purpose:
      "Industry-vertical discovery, fit-evaluation, and research planning (3-phase).",
    workflowRuleName: "industry-discovery-workflow",
  },
  {
    bundle: "maintenance-audit",
    skill: "/audit-docs",
    agent: "maintenance-audit",
    resolveOutputPath: (p) => `${p.docsRoot}/`,
    purpose:
      "Doc-registry audits with idempotent fixes (3-phase: scan, fix, verify). No new content.",
    workflowRuleName: "maintenance-audit-workflow",
  },
  {
    bundle: "meeting-analysis",
    skill: "/process-meeting",
    agent: "meeting-analyst",
    resolveOutputPath: (p) => `${p.meetingsRoot}/`,
    purpose:
      "Post-meeting transcript processing (4-phase: extract, notes, draft, link).",
    workflowRuleName: "meeting-processing-workflow",
  },
  {
    bundle: "people-profile",
    skill: "/profile-person",
    agent: "people-profile-analyst",
    resolveOutputPath: (p) => `${p.peopleRoot}/`,
    purpose:
      "Person profiles only (up to 4 phases incl. refresh). Hands off to company/software bundles.",
    workflowRuleName: "people-profile-workflow",
  },
  {
    bundle: "pr-review",
    skill: "/review-pr",
    agent: "pr-reviewer",
    resolveOutputPath: () => "",
    purpose:
      "PR review, auto-merge gating, and human-in-the-loop feedback orchestration.",
    workflowRuleName: "pr-review-workflow",
  },
  {
    bundle: "regulatory-research",
    skill: "/scan-regulatory-landscape",
    agent: "regulatory-research-analyst",
    resolveOutputPath: (p) => `${p.docsRoot}/regulations/`,
    purpose:
      "Regulation research, version pages, and impact analysis (3-phase). Documents, never interprets.",
    workflowRuleName: "regulatory-research-workflow",
  },
  {
    bundle: "requirements-analyst",
    skill: "/scan-requirements",
    agent: "requirements-analyst",
    resolveOutputPath: (p) => `${p.researchRequirementsRoot}/`,
    purpose:
      "Requirements gap discovery + draft proposals (2-phase: scan, draft-trace). Hands off to writer.",
    workflowRuleName: "requirements-analyst-workflow",
  },
  {
    bundle: "requirements-reviewer",
    skill: "/review-requirements",
    agent: "requirements-reviewer",
    resolveOutputPath: (p) => `${p.requirementsRoot}/`,
    purpose:
      "Requirements audit (12 checks) and narrow-edit deprecation. Never authors new requirement docs.",
    workflowRuleName: "requirements-reviewer-workflow",
  },
  {
    bundle: "requirements-writer",
    skill: "/write-requirement",
    agent: "requirements-writer",
    resolveOutputPath: (p) => `${p.requirementsRoot}/`,
    purpose:
      "Formal requirement document authoring across the 11-category taxonomy + four-tier classification.",
    workflowRuleName: "requirements-writer-workflow",
  },
  {
    bundle: "research-pipeline",
    skill: "/research",
    agent: "research-analyst",
    resolveOutputPath: (p) => `${p.researchRoot}/`,
    purpose:
      "Generic research micro-tasks (3-phase: scope, slice, verify). Notes-only output.",
    workflowRuleName: "research-pipeline-workflow",
  },
  {
    bundle: "software-profile",
    skill: "/profile-software",
    agent: "software-profile-analyst",
    resolveOutputPath: (p) => `${p.softwareRoot}/`,
    purpose:
      "Software profiles, shared feature matrix, and BCM-tree capability mapping (up to 5 phases).",
    workflowRuleName: "software-profile-workflow",
  },
  {
    bundle: "standards-research",
    skill: "/scope-standards-research",
    agent: "standards-research-analyst",
    resolveOutputPath: (p) => `${p.docsRoot}/standards/`,
    purpose:
      "Interoperability standards research (5-phase: scope, research, compare, extension, organizations).",
    workflowRuleName: "standards-research-workflow",
  },
];

/**
 * The set of `<bundle>-workflow` rule names that the registry
 * subsumes. Used both to suppress those rules from the Claude
 * platform output and to assert in tests that no bundle still
 * ships its prose summary into CLAUDE.md.
 */
export const SUPPRESSED_WORKFLOW_RULE_NAMES: ReadonlyArray<string> =
  AGENT_REGISTRY_ENTRIES.map((e) => e.workflowRuleName);

/**
 * Returns `true` when the supplied rule name belongs to a
 * phased-agent `<bundle>-workflow` rule whose routing summary now
 * lives in the shared `agent-registry` rule.
 */
export function isSuppressedWorkflowRule(name: string): boolean {
  return SUPPRESSED_WORKFLOW_RULE_NAMES.includes(name);
}

/**
 * Reverse map from a `<bundle>-workflow` rule name to its owning
 * bundle name. Used by the registry consolidation loop to detect
 * when a consumer has targeted a bundle with a
 * `features.customDocSections` entry — those bundles keep
 * rendering their workflow rule into CLAUDE.md so the consumer-
 * supplied prose has somewhere to live. Returns `undefined` for
 * any rule name that is not in the registry's suppression list.
 */
export function bundleNameForWorkflowRule(
  ruleName: string,
): string | undefined {
  const entry = AGENT_REGISTRY_ENTRIES.find(
    (e) => e.workflowRuleName === ruleName,
  );
  return entry?.bundle;
}

/**
 * Build the consolidated agent-registry rule for the supplied
 * resolved bundle list. The helper:
 *
 * 1. Filters `AGENT_REGISTRY_ENTRIES` to bundles that are actually
 *    active in the consumer's resolved bundle set (so a consumer
 *    that excludes `bcm-writer` doesn't see a row for it).
 * 2. Renders one alphabetically-sorted markdown table row per
 *    surviving entry, with skill / agent / bundle / output-path /
 *    purpose columns.
 * 3. Returns a single ALWAYS-scoped `agent-registry` rule whose
 *    body opens with a 2-3 sentence preamble explaining the
 *    routing model and pointing at `.claude/agents/<name>.md` for
 *    full prompts and the cross-cutting convention rules at the
 *    top of CLAUDE.md.
 *
 * Consumers with no phased-agent bundle active receive `undefined`
 * and the rule is dropped from the resolved set — there is no
 * routing to summarise.
 */
/**
 * Build the ALWAYS-scoped `conventions-registry` rule from the
 * final, post-transform set of rules that will ship to the Claude
 * platform. The registry lists every `FILE_PATTERN`-scoped rule
 * (slug, comma-joined `filePatterns`, one-line description) so an
 * agent whose current file does not happen to match any rule's
 * `paths:` still has a discoverable routing table back into the
 * full convention set under `.claude/rules/`.
 *
 * The helper:
 *
 * 1. Walks every rule currently in the resolved rule map.
 * 2. Filters to `scope === AGENT_RULE_SCOPE.FILE_PATTERN` rules
 *    whose `platforms.claude.exclude` is not `true` — suppressed
 *    rules (e.g. `<bundle>-workflow` rules subsumed by
 *    `agent-registry`) never appear, and ALWAYS-scoped rules are
 *    omitted because they already render in CLAUDE.md.
 * 3. Renders one alphabetically-sorted markdown row per surviving
 *    rule. Each row's first cell links to `.claude/rules/<name>.md`
 *    so a reader can click through. The `Auto-loads when editing`
 *    cell shows up to three `filePatterns`, truncated with `, …`
 *    and pointing at the rule for the full list when there are
 *    more.
 *
 * Returns `undefined` when no FILE_PATTERN rules survive the
 * filter (e.g. a minimal consumer with no on-demand rules) so the
 * caller can drop the rule from the map without emitting an empty
 * table.
 */
export function buildConventionsRegistryRule(
  rules: Iterable<AgentRule>,
): AgentRule | undefined {
  const entries: Array<AgentRule> = [];
  for (const rule of rules) {
    if (rule.scope !== AGENT_RULE_SCOPE.FILE_PATTERN) {
      continue;
    }
    if (rule.platforms?.claude?.exclude === true) {
      continue;
    }
    if (!rule.filePatterns || rule.filePatterns.length === 0) {
      continue;
    }
    entries.push(rule);
  }
  if (entries.length === 0) {
    return undefined;
  }
  entries.sort((a, b) => a.name.localeCompare(b.name));

  const lines: Array<string> = [
    "# Conventions Registry",
    "",
    "Each row below is a `FILE_PATTERN`-scoped convention rule that",
    "auto-loads on demand when the agent's current file matches one",
    "of the listed paths. Full rule bodies live under",
    "`.claude/rules/<slug>.md` so they only consume context when",
    "they apply. Consult the matching rule before doing work in any",
    "of the listed surfaces.",
    "",
    "| Rule | Auto-loads when editing | Purpose |",
    "| --- | --- | --- |",
  ];

  const MAX_PATTERNS_SHOWN = 3;
  for (const rule of entries) {
    const ruleLink = `[\`${rule.name}\`](.claude/rules/${rule.name}.md)`;
    const patterns = rule.filePatterns ?? [];
    let patternCell: string;
    if (patterns.length <= MAX_PATTERNS_SHOWN) {
      patternCell = patterns.map((p) => `\`${p}\``).join(", ");
    } else {
      const shown = patterns
        .slice(0, MAX_PATTERNS_SHOWN)
        .map((p) => `\`${p}\``)
        .join(", ");
      patternCell = `${shown}, … ([full list](.claude/rules/${rule.name}.md))`;
    }
    // Collapse any newlines or pipe characters in the description
    // so a multi-line / pipe-bearing description does not break
    // the markdown table row.
    const purposeCell = rule.description
      .replace(/\s+/g, " ")
      .replace(/\|/g, "\\|")
      .trim();
    lines.push(`| ${ruleLink} | ${patternCell} | ${purposeCell} |`);
  }

  return {
    name: "conventions-registry",
    description:
      "Routing table for every FILE_PATTERN-scoped convention rule. Lists each rule's slug, the file patterns that auto-trigger it, and a one-line purpose so agents whose current file does not match any rule's paths: can still discover the full convention set.",
    scope: AGENT_RULE_SCOPE.ALWAYS,
    content: lines.join("\n"),
    platforms: {
      cursor: { exclude: true },
    },
    tags: ["workflow"],
  };
}

export function buildAgentRegistryRule(
  bundles: ReadonlyArray<AgentRuleBundle>,
  paths: ResolvedAgentPaths,
): AgentRule | undefined {
  const activeNames = new Set(bundles.map((b) => b.name));
  const rows = AGENT_REGISTRY_ENTRIES.filter((e) =>
    activeNames.has(e.bundle),
  ).slice();
  if (rows.length === 0) {
    return undefined;
  }
  rows.sort((a, b) => a.bundle.localeCompare(b.bundle));

  const lines: Array<string> = [
    "# Agent Registry",
    "",
    "Each row below is the routing summary for one phased-agent",
    "bundle: the user-invocable skill, the sub-agent that handles",
    "the work, the output path the agent writes to, and a one-line",
    "purpose. Use the table to answer *which agent handles X* —",
    "full agent prompts live in `.claude/agents/<name>.md` and only",
    "load on delegation, so the table is the always-on routing",
    "signal.",
    "",
    "Cross-cutting policies that apply to every agent below —",
    "Progress File Convention, Shared-Editing Safety, Issue",
    "Templates, Skill Evals — are documented as their own top-level",
    "sections elsewhere in this file. The registry only captures",
    "routing; it does not duplicate convention prose.",
    "",
    "| Skill | Agent | Bundle | Output path | Purpose |",
    "| --- | --- | --- | --- | --- |",
  ];

  for (const row of rows) {
    const outputPath = row.resolveOutputPath(paths);
    const pathCell = outputPath.length > 0 ? `\`${outputPath}\`` : "—";
    lines.push(
      `| \`${row.skill}\` | \`${row.agent}\` | \`${row.bundle}\` | ${pathCell} | ${row.purpose} |`,
    );
  }

  return {
    name: "agent-registry",
    description:
      "Routing table mapping each phased-agent bundle to its primary skill, sub-agent, output path, and one-line purpose. Replaces the per-bundle workflow rules whose prose summaries duplicated the routing signal.",
    scope: AGENT_RULE_SCOPE.ALWAYS,
    content: lines.join("\n"),
    platforms: {
      cursor: { exclude: true },
    },
    tags: ["workflow"],
  };
}
